import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import type { AppDispatch } from '/business/shared/state/store';
import type { QuestionConfigQuestionType } from '/index';
import type { ElectricityHeatSteamLocation } from '/business/shared/state/selectors/application/electricityHeatSteam/electHeatSteamTypes';
import isApplicationAnswersEditableSelector from '/business/shared/state/selectors/application/workflow/isApplicationAnswersEditableSelector';

import formatTonnage from '/shared/functions/formatTonnage';

import LinkButton from '/shared/components/widgets/button/LinkButton';
import Button from '/shared/components/widgets/button/Button';
import EducationWidget from '/business/shared/components/widgets/education/EducationWidget';

import { urlApplicationRoot } from '/business/shared/util/Statics';
import { QUESTION_BRANCH_ACTIVITY } from '/shared/util/Statics';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import countriesSelector from '/shared/state/selectors/countriesSelector';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import getElectLocationsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectLocationsSelector';
import getElectNumLocationsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumLocationsSelector';
import getElectNumLocationsInputtedSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumLocationsInputtedSelector';
import saveLocation from '/business/shared/state/actions/basket/package2/saveLocation';

import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import StyledElecBreakout from './components/__styled__/StyledElecBreakout';
import StyledInfoList, { StyledInfoListItem } from './components/__styled__/StyledInfoList';
import StyledElecEmptyState from './components/__styled__/StyledElecEmptyState';
import StyledBasketTable from '/business/shared/components/widgets/basket-table/__styled__/StyledBasketTable';
import BasketClearItems from '/business/shared/components/widgets/basket-table/BasketClearItems';

const addPageLink = `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam/locations/locations-add`;

function ElectricityHeatSteamLocationsTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isEditable = useSelector(isApplicationAnswersEditableSelector);
  const appId = useSelector(getApplicationIdSelector);
  const locations = useSelector(getElectLocationsSelector);
  const countries = useSelector(countriesSelector);
  const { getUrlWithAppId } = useUrlBuilder();

  const handleRemove = useCallback(
    (location: ElectricityHeatSteamLocation) => {
      if ((location?.aggregated_kwh ?? 0) > 0) {
        return false;
      }
      dispatch(
        saveLocation({
          location,
          applicationId: appId,
          isDelete: true,
        }),
      );
    },
    [dispatch, appId],
  );

  const handleRemoveBulk = useCallback(() => {
    locations.map((location: ElectricityHeatSteamLocation) => {
      if ((location?.aggregated_kwh ?? 0) > 0) {
        return false;
      }
      dispatch(
        saveLocation({
          location,
          applicationId: appId,
          isDelete: true,
        }),
      );
    });
  }, [dispatch, appId, locations]);
  const getOnelineAddress = (location: ElectricityHeatSteamLocation) => {
    return [
      location.address1,
      location.address2,
      location.town,
      location.postcode,
      t(`countries:countryNames.${countries?.find(({ key }) => location.country_code === key)?.value}`),
    ]
      .filter(Boolean)
      .join(', ');
  };
  return (
    <>
      {isEditable && <BasketClearItems deleteCallbackButton={handleRemoveBulk} />}
      <StyledBasketTable>
        <thead>
          <tr>
            <th>{t('questions:electricityHeatSteam.locations.table.locationName')}</th>
            <th>{t('questions:electricityHeatSteam.locations.table.address')}</th>
            <th>{t('questions:electricityHeatSteam.locations.table.carbonEmissions')}</th>
            <th>&nbsp;</th>
            <th>&nbsp;</th>
          </tr>
        </thead>
        <tbody>
          {locations.map((location) => {
            const tonnageLineVal = location.aggregated_tonnage ? formatTonnage(location.aggregated_tonnage) : '-';
            const tonnageLineDescriptor = location.aggregated_tonnage
              ? t('business:emissionFactors.emissionUnits.tonnageRaw')
              : '';
            return (
              <tr key={`location-${location.id}`}>
                <td>{location.unique_name}</td>
                <td>{getOnelineAddress(location)}</td>
                <td>
                  {tonnageLineVal} {tonnageLineDescriptor}
                </td>
                <td>
                  <LinkButton
                    disabled={!isEditable}
                    $secondary
                    to={getUrlWithAppId(`${addPageLink}?edit=${location.id}`, appId)}
                  >
                    {t('questions:electricityHeatSteam.shared.edit')}
                  </LinkButton>
                </td>
                <td>
                  <Button
                    disabled={(location?.aggregated_tonnage ?? 0) > 0 || !isEditable}
                    $secondary
                    onClick={() => handleRemove(location)}
                  >
                    {t('questions:electricityHeatSteam.shared.remove')}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </StyledBasketTable>
    </>
  );
}

export default function ElectricityHeatSteamLocations({ question }: { question: QuestionConfigQuestionType }) {
  const { t } = useTranslation();
  const appId = useSelector(getApplicationIdSelector);
  const numLocations = useSelector(getElectNumLocationsSelector);
  const numLocationsInputted = useSelector(getElectNumLocationsInputtedSelector);
  const isEditable = useSelector(isApplicationAnswersEditableSelector);
  const { getUrlWithAppId } = useUrlBuilder();

  return (
    <>
      <EducationWidget question={question} branchType={QUESTION_BRANCH_ACTIVITY} />

      <div>
        <StyledHeading as="h1" $type="h1">
          {/* @i18n: Electricity, heat and steam - Locations */}
          <Trans i18nKey="questions:electricityHeatSteam.locations.title" />
        </StyledHeading>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          {/* @i18n: Add all locations covered in this carbon review to enter usage data for each. */}
          <Trans i18nKey="questions:electricityHeatSteam.locations.intro" />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>
            {/* @i18n: For more information, refer to the "Help" section for detailed guidance. */}
            <Trans i18nKey="questions:electricityHeatSteam.shared.helpText" />
          </div>
        </StyledInfoFlash>
        {numLocations > 0 && (
          <div style={{ paddingTop: '1rem' }}>
            <LinkButton disabled={!isEditable} $primary to={getUrlWithAppId(addPageLink, appId)}>
              {/* @i18n: Add a new location */}
              <Trans i18nKey="questions:electricityHeatSteam.locations.addNewLocation" />
            </LinkButton>
          </div>
        )}
      </div>

      {numLocations > 0 ? (
        <StyledElecBreakout>
          <StyledHeading as="h2" $type="h2">
            {/* @i18n: Locations added for electricity, heat and steam */}
            <Trans i18nKey="questions:electricityHeatSteam.locations.summaryTitle" />
          </StyledHeading>
          <StyledInfoList>
            <StyledInfoListItem>
              <div>{t('questions:electricityHeatSteam.shared.totalLocationsAdded')}</div>
              <div>{numLocations}</div>
            </StyledInfoListItem>
            <StyledInfoListItem>
              <div>{t('questions:electricityHeatSteam.shared.consumptionDataAddedFor')}</div>
              <div>{numLocationsInputted}</div>
            </StyledInfoListItem>
          </StyledInfoList>
          <ElectricityHeatSteamLocationsTable />
        </StyledElecBreakout>
      ) : (
        <StyledElecEmptyState>
          <StyledHeading as="h2" $type="h2">
            {/* @i18n: Add location details */}
            <Trans i18nKey="questions:electricityHeatSteam.locations.emptyTitle" />
          </StyledHeading>
          <StyledParagraph $smallText $hasExtraMarginBottom>
            {/* @i18n: Add all locations covered in this carbon review to enter usage data for each. */}
            <Trans i18nKey="questions:electricityHeatSteam.locations.intro" />
          </StyledParagraph>
          <div style={{ paddingTop: '1rem' }}>
            <LinkButton $primary to={getUrlWithAppId(addPageLink, appId)}>
              {/* @i18n: Add a new location */}
              <Trans i18nKey="questions:electricityHeatSteam.locations.addNewLocation" />
            </LinkButton>
          </div>
        </StyledElecEmptyState>
      )}
    </>
  );
}
